/**
 * UranIA PA — 404 (`*`, catch-all su `router.tsx`, `defaultNotFoundComponent`).
 *
 * "404 con ricerca e rientro all'atterraggio del ruolo" (Riordino della
 * mappa delle rotte §3, Rilascio A passo A.2). La "ricerca" non interroga
 * un indice — nessun endpoint di ricerca sito esiste oggi — è un filtro
 * client-side su una mappa statica delle destinazioni principali: basta
 * per orientare chi ha sbagliato indirizzo, senza inventare un servizio
 * che non c'è.
 *
 * Il link di rientro usa `smistaRadice(ruoloCorrente())`: stessa funzione
 * già in uso su `(pubblico)` — se l'utente è autenticato porta al suo
 * atterraggio, altrimenti alla vetrina pubblica.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import { ruoloCorrente } from '../../lib/auth/ruolo-corrente.ts';
import { smistaRadice } from '../../lib/ruoli.ts';

interface Destinazione {
  readonly label: string;
  readonly to: string;
}

// Mappa statica delle destinazioni principali (non un indice di ricerca —
// vedi commento in testa al file). Aggiornare quando una rotta elencata
// qui viene rinominata o fusa (Rilascio A passi A.4/A.5).
const DESTINAZIONI: readonly Destinazione[] = [
  { label: 'Vetrina pubblica', to: '/' },
  { label: 'Accedi', to: '/accedi' },
  { label: 'Servizi', to: '/servizi' },
  { label: 'Il mio lavoro', to: '/dashboard' },
  { label: 'Onboarding', to: '/onboarding' },
  { label: 'Ambiente immersivo', to: '/immersivo' },
  { label: 'Redazione assistita', to: '/cowork' },
  { label: 'Documenti (Mod5)', to: '/workspace/mod5' },
  { label: 'Amministrazione', to: '/admin' },
  { label: 'Cruscotto di piattaforma', to: '/admin/piattaforma' },
  { label: 'Banca dati e corpus', to: '/admin/corpus' },
  { label: 'Conformità', to: '/admin/conformita' },
  { label: 'La piattaforma', to: '/piattaforma' },
  { label: 'Trasparenza IA', to: '/trasparenza-ia' },
  { label: 'Accessibilità', to: '/accessibilita' },
  { label: 'Privacy', to: '/privacy' },
];

export function NotFoundPage(): JSX.Element {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');

  const backTo = useMemo(() => smistaRadice(ruoloCorrente()) ?? '/', []);

  const risultati = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length === 0) return DESTINAZIONI;
    return DESTINAZIONI.filter((d) => d.label.toLowerCase().includes(q));
  }, [query]);

  return (
    <section
      className="container py-5"
      data-testid="not-found-page"
      aria-labelledby="not-found-heading"
    >
      <div className="mx-auto text-center" style={{ maxWidth: 640 }}>
        <span
          className="badge mb-3"
          style={{
            backgroundColor: '#e7f1ff',
            color: '#084298',
            fontSize: '0.7rem',
            letterSpacing: '0.05em',
          }}
        >
          404
        </span>
        <h1 id="not-found-heading" className="h3 mb-2">
          {t('sistema.not_found.title')}
        </h1>
        <p className="text-muted mb-4">{t('sistema.not_found.body')}</p>

        <div className="mb-4 text-start">
          <label htmlFor="not-found-search" className="form-label small text-muted">
            {t('sistema.not_found.search_label')}
          </label>
          <input
            id="not-found-search"
            type="search"
            className="form-control"
            value={query}
            onChange={(e): void => {
              setQuery(e.target.value);
            }}
            placeholder={t('sistema.not_found.search_placeholder')}
            data-testid="not-found-search"
          />
          <ul className="list-unstyled mt-3 mb-0" data-testid="not-found-results">
            {risultati.length === 0 ? (
              <li className="text-muted small fst-italic">{t('sistema.not_found.search_empty')}</li>
            ) : (
              risultati.map((d) => (
                <li key={d.to} className="mb-1">
                  <Link to={d.to} className="text-decoration-none">
                    {d.label}
                  </Link>
                </li>
              ))
            )}
          </ul>
        </div>

        <Link to={backTo} className="btn btn-primary" data-testid="not-found-back">
          {t('sistema.not_found.back_to_landing')}
        </Link>
      </div>
    </section>
  );
}
